import React, { useEffect } from 'react'

import { getUrlByDomain } from '@js/common/getUrlByDomain'

import CTA from '@f/elements/CTA'
import Icon from '@f/elements/Icon'

const MonthlyRateCalculatorModal = ({
  housePrice,
  yearlyInterestRate,
  mortgageTermYears,
  extraCosts,
  equity,
  closeModal,
  expansionStage,
  fixedInterestRatePeriodYears,
  paymentToPrincipal,
  isModal,
  domain
}) => {
  const getUrl = getUrlByDomain(domain)
  const closeOverlay = () => {
    _wt({
      category: 'modal',
      action: 'hide',
      values: [
        {
          type: 'name',
          value: 'finance-monthlyrate'
        }
      ]
    })

    closeModal()
  }

  const getNebenkosten = (housePrice, extraCosts) => {
    return Number((housePrice * extraCosts).toFixed()).toLocaleString('de-DE')
  }

  const getEigenkapital = (housePrice, extraCosts, equity) => {
    let eigenkapital = equity * (housePrice + housePrice * extraCosts)
    return Number(eigenkapital.toFixed()).toLocaleString('de-DE')
  }

  const getNettodarlehen = (housePrice, extraCosts, equity) => {
    let nettodarlehen =
      housePrice +
      housePrice * extraCosts -
      equity * (housePrice + housePrice * extraCosts)
    return Number(nettodarlehen.toFixed()).toLocaleString('de-DE')
  }

  const getMonthlyRate = () => {
    /* r */ let monthlyInterestRate = yearlyInterestRate / 12
    /* n */ let mortgageTermMonths = mortgageTermYears * 12
    /* P */ let maxBorrowAmount =
      housePrice +
      extraCosts * housePrice -
      equity * (housePrice + equity * housePrice)

    /* M */ let monthlyRate =
      (monthlyInterestRate *
        (1 + monthlyInterestRate) ** mortgageTermMonths *
        maxBorrowAmount) /
      ((1 + monthlyInterestRate) ** mortgageTermMonths - 1)

    return Number(monthlyRate.toFixed()).toLocaleString('de-DE')
  }

  let formattedEquity = Number((equity * 100).toFixed(2)).toLocaleString(
    'de-DE'
  )
  let formattedYearlyInterestRate = Number(
    (yearlyInterestRate * 100).toFixed(2)
  ).toLocaleString('de-DE')
  let formattedExtraCosts = Number(
    (extraCosts * 100).toFixed(2)
  ).toLocaleString('de-DE')
  let formattedPaymentToPrincipal = Number(
    (paymentToPrincipal * 100).toFixed(2)
  ).toLocaleString('de-DE')

  useEffect(() => {
    _wt({
      category: 'modal',
      action: 'display',
      values: [
        {
          type: 'name',
          value: 'finance-monthlyrate'
        }
      ]
    })
  }, [])

  return (
    <div className='RequestWizard-wrapper'>
      <div className='RequestWizard'>
        <div className='RequestWizard-form-wrapper'>
          <div className='RequestWizard-top'>
            <div className='RequestWizard-heading'>
              <div className='[ f-t-tmedium f-c-fo-pri f-t-centre f-p-h-080:m ]'>
                Geschätzte Monatsrate
              </div>
            </div>

            <div className='RequestWizard-content'>
              <div className='[ f-t-mini f-t-centre ]'>
                <div className='[ f-t-tsection ] value'>{`${getMonthlyRate()} €`}</div>
                <div>Der Wert errechnet sich aus folgenden Annahmen*:</div>
              </div>
              <div className='[ f-p-t-020 f-m-v-020 f-c-bo-neu050 ] MonthlyRateCalculatorModal-table'>
                <div className='[ f-d-flr f-t-mini f-t-bold ]'>
                  <div className='[ f-fl-expand ]'>{`Preis ${expansionStage}`}</div>
                  <div>{`${Number(housePrice).toLocaleString('de-DE')} €`}</div>
                </div>
                <div className='[ f-d-flr f-t-mini ]'>
                  <div className='[ f-fl-expand ]'>Nebenkosten</div>
                  <div>{`+ ${getNebenkosten(housePrice, extraCosts)} €`}</div>
                </div>
                <div className='[ f-d-flr f-t-mini ]'>
                  <div className='[ f-fl-expand ]'>Eigenkapital</div>
                  <div>
                    {`- ${getEigenkapital(housePrice, extraCosts, equity)} €`}
                  </div>
                </div>
                <div className='[ f-d-flr f-t-mini ] nettodarlehen'>
                  <div className='[ f-fl-expand ]'>Nettodarlehen</div>
                  <div>
                    {`${getNettodarlehen(housePrice, extraCosts, equity)} €`}
                  </div>
                </div>
              </div>
            </div>

            <div className='RequestWizard-bottom'>
              <div className='[ f-t-centre f-t-mini f-c-fo-neu200 f-m-v-010 f-p-h-080:m ]'>
                Für eine komplette Übersicht zu Bau- und Finanzierungskosten
                empfehlen wir die Kontaktaufnahme zum Anbieter und die Beratung
                durch einen Finanzexperten.
              </div>
              <CTA
                type='block-primary'
                tag='a'
                href={getUrl('financeAssistance')()}
                data-sel='btn-cta'
                className='[ f-m-h-auto f-m-t-020 ] is-block:s'
                wtData={{
                  'CTA-click': {
                    label: 'finance-quiz-standalone',
                    location: 'modal-finance-monthlyrate'
                  }
                }}>
                Finanzierungen kostenlos vergleichen
              </CTA>

              <div className='[ f-t-centre f-t-micro f-c-fo-neu200 f-p-v-010 f-m-t-010 f-p-h-080:m ]'>
                {`*Basierend auf: ${formattedEquity}% Eigenkapital, ${formattedYearlyInterestRate}% Sollzins, ${fixedInterestRatePeriodYears} Jahre Sollzinsbindung, ${formattedPaymentToPrincipal}% Tilgungsrate, ${formattedExtraCosts}% Baunebenkosten`}
              </div>
            </div>
            {isModal && (
              <CTA
                type='icon'
                tag='button'
                className='Modal-top-button Modal-top-button-close'
                onClick={closeOverlay}
                data-sel='btn-cls'>
                <Icon icon='close-16px' className='[ f-icon-030 f-c-st-bla ]' />
              </CTA>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default MonthlyRateCalculatorModal
